import theme from "assets/theme";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    width: "100%",
    height: "600px",
    gap: 15,
  },
  searchContainer: {
    width: "100%",
    marginBottom: 10,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: "row",
    gap: 40,
  },
  column: {
    flex: 1,
    backgroundColor: theme.color.background,
    borderRadius: 5,
    padding: 10,
  },
  columnHeader: {
    fontSize: 16,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.darkGrey,
    marginBottom: 10,
    textAlign: "center",
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
  },
  listContainer: {
    flex: 1,
  },
  personRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    backgroundColor: theme.color.white,
    marginVertical: 2,
  },
  selectedPersonRow: {
    backgroundColor: theme.color.lightGrey,
  },
  personInfo: {
    flex: 1,
    marginLeft: 10,
  },
  personName: {
    fontSize: 14,
    fontWeight: theme.font.weight.medium,
    color: theme.color.text,
    flexWrap: "wrap",
    textBreakStrategy: "simple",
    lineBreakMode: "wordWrap",
  },
  personEmail: {
    fontSize: 12,
    color: theme.color.darkGrey,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  checkedBox: {
    backgroundColor: theme.color.primary,
    borderColor: theme.color.primary,
  },
  uncheckedBox: {
    backgroundColor: theme.color.white,
    borderColor: theme.color.grey,
  },
  checkIcon: {
    color: theme.color.white,
    fontSize: 14,
    fontWeight: "bold",
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  emptyText: {
    fontSize: 14,
    color: theme.color.darkGrey,
    textAlign: "center",
  },
});

export default function PersonList({
  personList,
  teamId,
  checkedPersons,
  setCheckedPersons,
  setCanSave,
}) {
  const { teamList, organizationColor } = useContext(OrganizationContext);
  const [searchValue, setSearchValue] = useState("");
  const [initialPersonIds, setInitialPersonIds] = useState([]);

  useEffect(() => {
    if (teamId && teamList.length > 0) {
      const team = teamList.find((thisTeam) => thisTeam.teamId === teamId);
      const currentPersonIds = team?._associations?.TeamPersons
        ? team._associations.TeamPersons.map((tp) => tp.personId)
        : [];
      setInitialPersonIds(currentPersonIds);
      setCheckedPersons(currentPersonIds);
    }
  }, [teamId, teamList, setCheckedPersons]);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        checkedBox: {
          ...classes.checkedBox,
          backgroundColor: organizationColor,
          borderColor: organizationColor,
        },
      }),
    [organizationColor],
  );

  const filteredPersons = useMemo(() => {
    let filtered = personList;

    if (searchValue.trim()) {
      filtered = personList.filter((person) => {
        const name =
          `${person.firstName || ""} ${person.lastName || ""}`.toLowerCase();
        const email = (person.email || "").toLowerCase();
        const searchTerm = searchValue.toLowerCase();

        return name.includes(searchTerm) || email.includes(searchTerm);
      });
    }

    return filtered;
  }, [personList, searchValue]);

  const currentMembers = useMemo(() => {
    return filteredPersons
      .filter((person) => checkedPersons.includes(person.personId))
      .sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [filteredPersons, checkedPersons]);

  const availablePersons = useMemo(() => {
    return filteredPersons
      .filter((person) => !checkedPersons.includes(person.personId))
      .sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [filteredPersons, checkedPersons]);

  useEffect(() => {
    const hasChanges = !arraysEqual(
      checkedPersons.sort(),
      initialPersonIds.sort(),
    );
    setCanSave(hasChanges);
  }, [checkedPersons, initialPersonIds, setCanSave]);

  function arraysEqual(a, b) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((val, index) => val === b[index]);
  }

  const togglePerson = (personId) => {
    setCheckedPersons((prev) => {
      if (prev.includes(personId)) {
        return prev.filter((id) => id !== personId);
      } else {
        return [...prev, personId];
      }
    });
  };

  const renderPersonItem = ({ item: person }) => {
    const isChecked = checkedPersons.includes(person.personId);
    const personName =
      `${person.firstName || ""} ${person.lastName || ""}`.trim();

    return (
      <TouchableOpacity
        style={classes.personRow}
        onPress={() => togglePerson(person.personId)}
      >
        <View
          style={[
            classes.checkbox,
            isChecked ? stateClasses.checkedBox : classes.uncheckedBox,
          ]}
        >
          {isChecked && <Text style={classes.checkIcon}>✓</Text>}
        </View>

        <View style={classes.personInfo}>
          <Text style={classes.personName}>{personName || "Unknown"}</Text>
          {person.email && (
            <Text style={classes.personEmail}>{person.email}</Text>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderColumn = (data, title) => (
    <View style={classes.column}>
      <Text style={[classes.columnHeader, { color: organizationColor }]}>
        {title} ({data.length})
      </Text>
      <View style={classes.listContainer}>
        {data.length > 0 ? (
          // <FlatList
          //   data={data}
          //   renderItem={renderPersonItem}
          //   keyExtractor={(person) => person.personId}
          //   showsVerticalScrollIndicator={true}
          // />
          <ScrollView
            style={{ maxHeight: 390 }}
            contentContainerStyle={{ paddingBottom: 10 }}
          >
            {data.map((person) => (
              <View key={person.personId}>
                {renderPersonItem({ item: person })}
              </View>
            ))}
          </ScrollView>
        ) : (
          <View style={classes.emptyState}>
            <Text style={classes.emptyText}>
              {searchValue
                ? "No people found matching your search"
                : `No ${title.toLowerCase()}`}
            </Text>
          </View>
        )}
      </View>
    </View>
  );

  return (
    <View style={classes.root}>
      <View style={classes.searchContainer}>
        <SearchBar
          placeholder="Search people..."
          value={searchValue}
          onChangeText={setSearchValue}
        />
      </View>

      <View style={classes.columnsContainer}>
        {renderColumn(availablePersons, "Available People")}
        {renderColumn(currentMembers, "Current Members")}
      </View>
    </View>
  );
}
